'use client';

import { HugeiconsIcon } from '@hugeicons/react';
import {
  FavouriteIcon,
  ShoppingCart01Icon,
  UserIcon,
} from '@hugeicons/core-free-icons';
import Link from 'next/link';
import React, { MouseEvent, useEffect, useState } from 'react';
import NextImage from '@/features/Image';

// 1. Import the new components and styles
import CartDrawer from './CartDrawer';
import { useFetcherCSR } from '@/lib/useFetcherCSR';
import { useAuth } from '@/features/authentication/hooks/auth';
import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import { fetchCartItems } from '@/lib/redux/features/cart';
import { pushToDataLayer, toGa4CartItem } from '@/features/gtm/gtmUtils';
import { usePathname, useRouter } from 'next/navigation';
import { API_WISHLISTS } from '@/constant/apiRout';

const HeaderCart: React.FC = () => {
  const { user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const { data: wishlists } = useFetcherCSR(user && API_WISHLISTS);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const despatch = useAppDispatch();
  const items = useAppSelector((state) => state.cart.items);
  const [isCartHydrated, setIsCartHydrated] = useState(false);
  const [shouldTrackCartView, setShouldTrackCartView] = useState(false);

  const total = items.reduce(
    (acc, curr) => acc + curr.price * curr.quantity,
    0
  );
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const cartPageViewFired = React.useRef(false);

  useEffect(() => {
    setTotalQuantity(items.reduce((total, item) => total + item.quantity, 0));
  }, [items]);

  useEffect(() => {
    let isActive = true;
    setIsCartHydrated(false);
    despatch(fetchCartItems()).then((action) => {
      if (isActive) {
        setIsCartHydrated(fetchCartItems.fulfilled.match(action));
      }
    });

    return () => {
      isActive = false;
    };
  }, [despatch, user?.data?.id]);

  useEffect(() => {
    if (pathname !== '/cart') {
      cartPageViewFired.current = false;
    }

    if (
      !isCartHydrated ||
      (!shouldTrackCartView &&
        (pathname !== '/cart' || cartPageViewFired.current))
    ) {
      return;
    }

    pushToDataLayer('view_cart', {
      ecommerce: {
        value: total,
        currency: 'BDT',
        items: items.map((item) =>
          toGa4CartItem(item),
        ),
      },
    });
    if (shouldTrackCartView) {
      setShouldTrackCartView(false);
    } else {
      cartPageViewFired.current = true;
    }
  }, [isCartHydrated, shouldTrackCartView, items, total, pathname]);

  useEffect(() => {
    if (isCartDrawerOpen) {
      // 1. Calculate the width of the scrollbar
      // We do this by comparing the window's full width with the client's width (which excludes the scrollbar)
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      // 2. Add the 'overflow: hidden' class
      document.body.classList.add('cart-drawer-is-open');

      // 3. Apply the scrollbar's width as padding to the body
      // This prevents the page content from jumping to the right
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      // When the drawer closes, remove the class and the padding
      document.body.classList.remove('cart-drawer-is-open');
      document.body.style.paddingRight = ''; // Reset the padding
    }

    // Cleanup function to ensure styles are removed if the component unmounts
    return () => {
      document.body.classList.remove('cart-drawer-is-open');
      document.body.style.paddingRight = '';
    };
  }, [isCartDrawerOpen]);

  const handleCartClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // if (!user) return router.push(`/login`);
    // Prevent the link from navigating to a new page
    e.preventDefault();
    // Open the drawer instead
    setIsCartDrawerOpen(true);
    setShouldTrackCartView(true);
  };
  const wishlistsLength = wishlists?.data?.length || 0; // Static value
  return (
    <>
      {/* Wishlist Icon */}
      <Link
        className="header__action-btn  d-xl-grid cart-link"
        href="/wishlist"
      >
        <HugeiconsIcon icon={FavouriteIcon} />
        {wishlistsLength > 0 && (
          <span className="cart-count">{wishlistsLength}</span>
        )}
      </Link>

      <Link
        href="/cart"
        className="header__action-btn d-xl-grid cart-link"
        onClick={handleCartClick}
        aria-label="Open shopping cart"
      >
        <HugeiconsIcon icon={ShoppingCart01Icon} />
        {totalQuantity > 0 && (
          <span className="cart-count">{totalQuantity}</span>
        )}
      </Link>

      {/* User Icon */}
      <Link className="header__action-btn d-xl-grid" href="/account">
        {user?.data?.avatar ? (
          <div
            className="d-inline-block rounded-circle overflow-hidden"
            style={{ width: '26.18px', height: '26.18px' }}
          >
            <NextImage
              src={user.data.avatar}
              alt={'user Image'}
              width={26.18}
              height={26.18}
              className="w-100 h-100 border-2"
              style={{ objectFit: 'cover' }}
            />
          </div>
        ) : (
          <HugeiconsIcon icon={UserIcon} />
        )}
      </Link>

      {/* 4. Render the CartDrawer component */}
      <CartDrawer
        isOpen={isCartDrawerOpen}
        onClose={() => setIsCartDrawerOpen(false)}
      />
    </>
  );
};

export default HeaderCart;
